
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  ChevronLeft, 
  LayoutDashboard, 
  MessageSquare, 
  Upload, 
  CreditCard, 
  Settings, 
  HelpCircle, 
  LogOut 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface SidebarProps {
  className?: string;
}

interface SidebarItem {
  icon: React.ElementType;
  label: string;
  path: string;
}

const mainNavItems: SidebarItem[] = [
  {
    icon: LayoutDashboard,
    label: 'Dashboard',
    path: '/dashboard',
  },
  {
    icon: MessageSquare,
    label: 'Interview',
    path: '/chat',
  },
  {
    icon: Upload,
    label: 'Upload Images',
    path: '/upload',
  },
  {
    icon: CreditCard,
    label: 'Subscription',
    path: '/subscription',
  },
];

const bottomNavItems: SidebarItem[] = [
  {
    icon: Settings,
    label: 'Settings',
    path: '/settings',
  },
  {
    icon: HelpCircle,
    label: 'Help & Support',
    path: '/help',
  },
  {
    icon: LogOut,
    label: 'Logout',
    path: '/logout',
  },
];

export const Sidebar: React.FC<SidebarProps> = ({ className }) => {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  return (
    <div
      className={cn(
        'flex flex-col h-screen sticky top-0 z-40 transition-all duration-300 ease-in-out',
        collapsed ? 'w-[70px]' : 'w-[250px]',
        'bg-sidebar border-r border-sidebar-border',
        className
      )}
    >
      <div className="flex items-center justify-between h-16 px-4 border-b border-sidebar-border">
        {!collapsed && (
          <Link to="/dashboard" className="font-semibold text-lg">
            EmergencyPlan
          </Link>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="ml-auto"
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <ChevronLeft
            className={cn(
              'h-5 w-5 text-sidebar-foreground/70 transition-transform',
              collapsed && 'rotate-180'
            )}
          />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto py-4 px-3">
        <nav className="space-y-1">
          {mainNavItems.map((item, index) => (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                'flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors',
                isActive(item.path)
                  ? 'text-sidebar-primary bg-sidebar-accent'
                  : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50',
                collapsed && 'justify-center'
              )}
            >
              <item.icon className={cn('h-5 w-5', !collapsed && 'mr-3')} />
              {!collapsed && <span>{item.label}</span>}
            </Link>
          ))}
        </nav>
      </div>

      <div className="p-3 border-t border-sidebar-border">
        <nav className="space-y-1">
          {bottomNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                'flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors',
                isActive(item.path)
                  ? 'text-sidebar-primary bg-sidebar-accent'
                  : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50',
                collapsed && 'justify-center'
              )}
            >
              <item.icon className={cn('h-5 w-5', !collapsed && 'mr-3')} />
              {!collapsed && <span>{item.label}</span>}
            </Link>
          ))}
        </nav>
      </div>
    </div>
  );
};

export default Sidebar;
